import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { notificationsService } from '../services/notifications.service';
import type { Notification } from '../services/notifications.service';

/**
 * Hook para obtener notificaciones del usuario
 */
export function useNotifications(limit = 20, includeRead = false) {
  return useQuery({
    queryKey: ['notifications', limit, includeRead],
    queryFn: () => notificationsService.getMyNotifications(limit, includeRead),
    refetchInterval: 30000, // Refetch cada 30 segundos
  });
}

/**
 * Hook para obtener cantidad de notificaciones no leídas
 */
export function useUnreadNotifications() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsService.getUnreadCount(),
    refetchInterval: 30000, // Refetch cada 30 segundos
  });
}

/**
 * Hook para marcar notificación como leída
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationsService.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });
}

/**
 * Hook para marcar todas las notificaciones como leídas
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Todas las notificaciones marcadas como leídas');
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });
}

/**
 * Hook para eliminar notificación
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationsService.delete(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Notificación eliminada');
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });
}

/**
 * Hook para suscribirse a notificaciones en tiempo real
 */
export function useRealtimeNotifications(userId: string | undefined) {
  const queryClient = useQueryClient();
  const [latestNotification, setLatestNotification] = useState<Notification | null>(null);

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = notificationsService.subscribeToNotifications(
      userId,
      (notification) => {
        // Actualizar queries
        queryClient.invalidateQueries({ queryKey: ['notifications'] });

        // Guardar última notificación para mostrar toast
        setLatestNotification(notification);

        // Mostrar toast
        toast.info(notification.titulo, {
          description: notification.mensaje,
          duration: 5000,
        });
      }
    );

    return unsubscribe;
  }, [userId, queryClient]);

  return { latestNotification };
}

